import { settingsSearchHref, type SearchHit } from '../shell/globalSearch';

/**
 * 🔒 The Settings Index's search (piece 15; decision 1397, extending 0271; T582,
 * proposal C1). Typing filters the Index to the entries whose title or words
 * hold every typed word, the way the Mail Settings window's search does, and a
 * tile with no match is hidden. The tile layout of decisions 0271 and 0282 is
 * unchanged: the search chooses which cards stand, never where they stand.
 *
 * 🔒 The words are the section and field names each entry's page holds, kept
 * in this one list beside the Index rows and keyed by the row's route, so
 * *mileage* finds Expenses and *footer* finds Email templates and footer.
 * 🔨 A word names something a person reads on the page, never a Settings key
 * or a column. A test reads the Index's seed and refuses a route here that no
 * row carries, so a moved entry cannot leave its words behind.
 */
export const SETTINGS_ENTRY_WORDS: Readonly<Record<string, readonly string[]>> = {
  // Organization
  '/settings/company/organisation': ['logo', 'time zone', 'organisation', 'company', 'legal name', 'trading name', 'date format', 'quantity decimals', 'precision', 'fiscal year', 'po threshold', 'domain', 'register', 'address', 'ein', 'duns'],
  '/org': ['organization chart', 'reports to', 'manager'],
  '/settings/people/departments': ['department', 'team', 'head'],
  // Decision 10100: the kinds of place a site can be.
  '/settings/organization/site-types': ['site type', 'site category', 'office', 'plant', 'facility', 'lift station'],
  // People
  '/settings/people/confidentiality-roles': ['confidential', 'nda', 'non-disclosure', 'role definitions'],
  '/settings/people/roles': ['permissions', 'module access', 'capability', 'hr role'],
  '/settings/people/logins': ['password', 'user', 'lockout', 'time clock', 'sign in'],
  // Security
  '/settings/security': ['audit read retention', 'session lifetime', 'idle', 'break-glass', 'alert', 'confidential field', 'disclosure', 'reveal tail'],
  '/settings/security/screening': ['spam', 'signal', 'weight', 'threshold', 'hold'],
  '/settings/security/audit': ['history', 'who changed', 'log'],
  // Mobile
  '/settings/mobile': ['phone app', 'offline', 'sync', 'device session', 'minimum build'],
  '/settings/security/shared-under-nda': ['nda', 'released files', 'non-disclosure'],
  // Accounting
  '/settings/accounting/document-rules': ['auto-conversion rules', 'convert', 'quote to order', 'stale send warning', 'stale', 'unsent', 'warning', 'rfq chase defaults', 'rfq', 'chase', 'vendor reply', 'reminder', 'non-returnable line on a credit note', 'credit note', 'non-returnable', 'return'],
  '/settings/accounting/number-series': ['numbering', 'prefix', 'document number'],
  '/settings/accounting/approvals': ['approval rules', 'approval tiers', 'chain depth', 'approver'],
  '/settings/accounting/customer-credit': ['credit limit', 'finance charge', 'overdue', 'escalation'],
  '/settings/documents/shipment-preferences': ['shipping', 'carrier', 'our truck', 'delivery'],
  '/settings/accounting/package-types': ['package', 'box', 'pallet', 'crate', 'envelope', 'tube'],
  '/settings/accounting/basis': ['reporting basis', 'cost basis', 'cash basis', 'accrual', 'cogs'],
  '/settings/accounting/earning-code-map': ['payroll', 'earning code', 'wages'],
  '/settings/accounting/documents-inbox': ['inbox', 'master mailbox', 'accounting mailbox', 'dedicated address', 'subject line', 'bills email'],
  '/settings/accounting/budgets': ['budget', 'forecast'],
  // Banking & Payments
  '/settings/banking/payment-rules': ['bill variance tolerance', 'bill', 'variance', 'tolerance', 'three-way match', 'match confidence tiers', 'bank match', 'confidence', 'reconcile', 'check numbering direction', 'cheque', 'check number', 'ascending', 'descending', 'card surcharge', 'credit card', 'surcharge', 'debit', 'prepaid', 'card fee'],
  '/settings/accounting/expense-categories': ['mileage', 'mileage rate', 'per diem', 'per diem rates', 'expense categories', 'receipt'],
  // Tax
  '/settings/tax/sales-tax': ['tax rates', 'register', 'nexus', 'exemption forms', 'state'],
  '/settings/company/tax-exempt-certificates': ['exemption', 'resale certificate', 'exempt'],
  '/settings/accounting/form-1099-threshold': ['1099', 'contractor', 'filing'],
  '/settings/tax/our-exemption': ['exemption number', 'resale', 'manufacturing', 'purchase order', 'signer', 'license'],
  // CRM
  '/settings/crm/pipeline-rules': ['quick deal rules', 'quick deal', 'deal', 'follow-up rules', 'follow-up', 'reminder', 'touch', 'equipment photos', 'photo kinds', 'existing equipment'],
  '/settings/crm/tags': ['tag', 'label'],
  '/settings/crm/custom-fields': ['custom field', 'extra field'],
  '/settings/crm/stage-actions': ['deal stage', 'action', 'pipeline'],
  // Decision 5450: the Discovery library's page.
  '/settings/crm/discovery-templates': ['discovery', 'template', 'question', 'upload point', 'photo', 'visit'],
  '/settings/crm/website-lead-routing': ['lead routing', 'blocklist', 'web form', 'leads'],
  '/settings/crm/stage-questions': ['deal stage', 'question', 'checklist'],
  '/settings/crm/company-role-types': ['role type', 'customer', 'vendor', 'relationship'],
  '/settings/crm/territories': ['territory', 'region', 'check-in', 'retention', 'distance tolerance'],
  '/settings/crm/sales-goals': ['quota', 'target', 'goal'],
  '/settings/company/account-assignments': ['account owner', 'sales rep', 'assignment'],
  '/companies/duplicates': ['duplicate', 'merge', 'same company', 'same person', 'match'],
  '/settings/accounting/commission-rate-table': ['commission', 'rate', 'sales rep'],
  // Service
  '/settings/service/dispatch-rules': ['emergency approvals', 'emergency', 'retrospective approval', 'after hours', 'schedule notices', 'schedule', 'notice', 'quiet', 'drag', 'visit moved', 'workers told', 'agreement renewals', 'renewal reminder', 'on-call', 'on call', 'backup after', 'escalation', 'travel billing', 'travel', 'mileage billing', 'drive time'],
  '/settings/accounting/priority-tiers': ['priority', 'response time', 'work order'],
  '/settings/accounting/labour-and-skill-levels': ['labor', 'labour', 'skill level', 'modifier', 'rate'],
  '/settings/accounting/follow-up-reason-codes': ['reason code', 'follow-up', 'return visit'],
  '/settings/company/site-requirements': ['site', 'requirement types', 'badge', 'safety'],
  // Catalog
  '/settings/catalog/categories': ['category', 'attributes', 'item', 'catalog tree'],
  '/settings/catalog/attribute-types': ['attribute', 'unit', 'item field'],
  '/settings/catalog/pricing-defaults': ['margin', 'cost projection', 'price staleness', 'markup'],
  '/settings/catalog/import-price-list': ['price list', 'vendor prices', 'spreadsheet', 'import'],
  // Documents
  '/settings/documents/file-types': ['file classification', 'blocked extensions', 'upload'],
  '/settings/documents/release': ['release', 'watermark', 'download', 'share link', 'expiry'],
  '/settings/documents/classes': ['document class', 'role', 'visibility'],
  '/settings/documents/reading': ['ocr', 'vision', 'scan', 'reading key'],
  '/settings/documents/quarantine': ['malware', 'virus', 'quarantine'],
  '/settings/accounting/photo-processing': ['photo', 'image', 'resize', 'receipt photo'],
  '/settings/documents/print-templates': ['layout', 'template', 'pdf', 'letterhead', 'print defaults', 'paper size', 'margins', 'print'],
  '/settings/documents/envelopes': ['envelope', 'window', 'address block', 'mailing'],
  // Terms & Conditions
  '/settings/terms': ['terms and conditions', 'clause', 'section', 'preamble'],
  '/settings/terms/signature-block': ['signature', 'signer', 'signatory'],
  // Email
  '/mail/settings': ['mailbox', 'sending', 'signature', 'retention', 'mail', 'certificate'],
  '/settings/accounting/email-templates': ['footer', 'legal footer', 'template', 'email body'],
  // Notifications
  '/settings/notifications': ['reminder', 'notification', 'alert'],
  // HR (piece 34): the three built entries, one page with a section each since decision 2790.
  '/settings/hr/hr-rules': ['external escalation route', 'complaint', 'outside counsel', 'employment counsel', 'escalation', 'sole hr holder', 'hr document retention', 'retention', 'personnel file', 'purge', 'years after termination', 'hr day counts', 'certification lead time', 'retaliation', 'acknowledgement reminder', 'awaiting delivery', 'absence escalation', 'days', 'substance policy', 'drug test', 'drug and alcohol', 'consequence table', 'testing procedure', 'punch radius', 'gate punch', 'distance from the site', 'site pin'],
  '/settings/hr/employment-rules': ['employment rules', 'jurisdiction', 'state', 'locality', 'overtime', 'weekly overtime', 'break', 'meal break', 'rest break', 'final pay', 'sick leave', 'unemployment', 'drug testing', 'postings', 'pennsylvania', 'no employment rules'],
  '/settings/hr/time-off-plans': ['time off', 'pto', 'paid time off', 'vacation', 'sick leave', 'leave plan', 'accrual', 'cap', 'balance', 'negative balance', 'payout', 'enrollment'],
  '/settings/hr/job-roles': ['job role', 'job title', 'duties', 'duty description', 'workers compensation', 'class code', 'pay basis', 'overtime class', 'exempt', 'hire'],
  '/settings/hr/work-calendar': ['clock device', 'gate', 'terminal', 'tablet', 'kiosk', 'gate sign-in', 'time clock'],
  // Work (decision 3940)
  '/settings/work/job-types': ['job type', 'estimated minutes', 'length', 'task', 'skill level', 'sub-skill', 'part', 'checklist template', 'training'],
  // Knowledge base
  '/settings/knowledge/quiz-flag': ['quiz', 'failure rate', 'minimum attempts', 'training'],
  // Modules
  '/settings/modules': ['e-signature', 'budgets', 'depreciation', 'feature switch', 'turn on'],
  // Integrations
  // 🔒 Decision 1648: one entry with a card per vendor, so it carries every vendor's words.
  '/settings/integrations': [
    'stripe', 'plaid', 'zoho', 'payment processor', 'card payments', 'bank feed', 'webhook', 'api key', 'map', 'google', 'openstreetmap', 'travel buffer',
    'sso', 'single sign-on', 'microsoft', 'identity provider', 'clamav', 'virus', 'scanner', 'e-signature', 'accounting sync',
  ],
  '/settings/system/import-mappings': ['account type map', 'item field map', 'matching rules', 'import'],
  // System
  '/settings/system/system-errors': ['error log', 'failure', 'crash'],
  '/settings/system/setup-and-how-to': ['setup', 'how to', 'guide', 'steps', 'stepper', 'walkthrough'],
  '/settings/system/server-info': ['server', 'log', 'api log', 'console', 'timings', 'slow', 'performance', 'memory', 'disk', 'database size', 'uptime', 'status', 'stand-in', 'services', 'health'],
  '/settings/system/developer-routes': ['developer', 'reset', 'outbox'],
  '/settings/documents/storage': ['backup', 'storage', 'bucket', 'restore', 'rehearsal'],
  '/settings/system/scheduled-jobs': ['nightly', 'job', 'run hour', 'run now'],
  '/admin/trash': ['deleted', 'restore', 'purge', 'recycle bin'],
  '/settings/system/history': ['history', 'changed', 'who changed', 'recent', 'changes'],
};

/** The typed words, lower-cased, with nothing but spaces between them. */
function typedWords(term: string): string[] {
  return term.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/**
 * The rows a typed term keeps, in the order they came: a row stands when every
 * typed word is found in its title or its words, and an empty term keeps every
 * row.
 */
export function searchSettingsIndex<R extends { title: string; route: string }>(term: string, rows: readonly R[]): R[] {
  const typed = typedWords(term);
  if (typed.length === 0) return [...rows];
  return rows.filter((row) => {
    const haystack = [row.title, ...(SETTINGS_ENTRY_WORDS[row.route] ?? [])].join(' ').toLowerCase();
    return typed.every((word) => haystack.includes(word));
  });
}

/**
 * 🔒 The top bar's search over Settings (decision 2770; T1616): the Index rows a typed term keeps, by the Index
 * search's own rule, as the panel's Settings group. Each hit opens the entry's page; the group names its whole
 * total, and its See all opens the Index with the term in its search box.
 */
export function settingsSearchHits<R extends { id: string; title: string; route: string }>(term: string, rows: readonly R[]): { hits: SearchHit[]; total: number; seeAll: string } {
  const kept = searchSettingsIndex(term, rows);
  return {
    hits: kept.map((row) => ({ id: row.id, group: 'Settings', label: row.title, route: row.route })),
    total: kept.length,
    seeAll: settingsSearchHref(term),
  };
}
